import styled from '@emotion/styled';
import { css } from '@emotion/react';

import theme from 'styles/theme';

export type PaginationSize = 'sm' | 'md';

type ButtonProps = {
  $size: PaginationSize;
  $active?: boolean;
  $nav?: boolean;
  $color?: string;
  $activeColor?: string;
  $activeBackgroundColor?: string;
};

const sizeStyles: Record<PaginationSize, ReturnType<typeof css>> = {
  sm: css`
    min-width: 28px;
    height: 28px;
    font-size: 13px;
  `,
  md: css`
    min-width: 32px;
    height: 32px;
    font-size: 14px;
  `,
};

export const Container = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export const List = styled.ul`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Item = styled.li`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const NavIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

export const PageButton = styled('button', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding: 0;
  font-family: 'Mangueira', sans-serif;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  background: transparent;
  color: ${({ $color }) => $color || '#575757'};
  transition: opacity 0.2s ease, background 0.2s ease, color 0.2s ease;

  ${({ $size, $nav }) =>
    $nav
      ? css`
          min-width: 20px;
          height: ${$size === 'sm' ? '28px' : '32px'};
          font-size: ${$size === 'sm' ? '14px' : '16px'};
          font-weight: 400;
          letter-spacing: -0.5px;
        `
      : css`
          ${sizeStyles[$size]}
          border-radius: 50%;
        `}

  ${({ $active, $activeColor, $activeBackgroundColor, $nav }) =>
    $active &&
    !$nav &&
    css`
      background: ${$activeBackgroundColor || theme.colors.blue};
      color: ${$activeColor || '#fff'};
      font-weight: 600;
    `}

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }
`;

export const Ellipsis = styled.span<{ $size: PaginationSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  color: #575757;
  font-family: 'Mangueira', sans-serif;

  ${({ $size }) => sizeStyles[$size]}
`;
